<template>
    <div class="product-search-form">
        <form @submit.prevent="search()">
            <div class="input-group-flex">
                <input ref="input"
                       class="form-control input-xl"
                       data-testid="product-search-input"
                       :placeholder="inputPlaceholder"
                       :value="searchTerm"
                       :disabled="!isOnline"
                       @input="onInput" />
                <SquareBtn :color="btnColor"
                           :disabled="!isOnline || !searchTerm"
                           data-testid="product-search-btn"
                           data-analytics-id="Search / Products / Search"
                           size="xl"
                           class="btn--inline-flex"
                           :loading="searching"
                           @click="search">
                    {{ l('SEARCH_MODAL.SEARCH') }}
                </SquareBtn>
                <SquareBtn :color="btnColor"
                           :disabled="!isOnline || !searchTerm"
                           data-testid="product-search-clear-btn"
                           size="xl"
                           class="btn--inline-flex btn-white"
                           @click="reset">
                    {{ l('CLEAR') }}
                </SquareBtn>
            </div>
        </form>

        <ProductItem v-if="productsList"
                     class="mt-1"
                     :products-list="productsList"
                     @item-clicked="itemClicked"></ProductItem>
        <div v-else-if="productsList === false || error"
             class="alert alert-danger mt-1">
            <i class="fa-solid fa-circle-xmark"></i> {{ error || l('SEARCH_MODAL.NO_RESULTS_PRODUCTS') }}
        </div>
    </div>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import ItemType from '@/lib/Items/ItemType';
import ProductItem from '@/epos/vue/modals/Search/ProductItem';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { normalizeSearchQuery } from '@/functions/strings';
import { searchCategoryItemListCollection } from '@/lib/Items';

export default {
    name: 'ProductSearchForm',

    components: { ProductItem, SquareBtn },

    props: {
        btnColor: {
            type: String,
            default: 'primary',
        },
    },

    data() {
        return {
            searching: false,
            searchTerm: '',
            productsList: null,
            error: null,
        };
    },

    computed: {
        ...mapState(['isOnline', 'currentBranch']),

        inputPlaceholder() {
            return container.langService.getText('SEARCH_MODAL.PRODUCT_SEARCH_INPUT_PLACEHOLDER');
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.$refs.input.focus();
        });
    },

    methods: {
        onInput(evt) {
            this.productsList = null;
            this.error = null;
            this.searchTerm = evt.target.value;
        },

        search() {
            this.searching = true;
            this.error = null;
            const getSaleableItems = container.saleableItemService.itemTypes;
            const getAllProducts = getSaleableItems[ItemType.PRODUCT];
            const getAllFoodDrinks = getSaleableItems[ItemType.PRODUCT_FOOD];
            const getSearchTerm = normalizeSearchQuery(this.searchTerm);
            const searchResultsProducts = searchCategoryItemListCollection(
                getAllProducts,
                getSearchTerm
            );
            const searchResultsFoodDrink = searchCategoryItemListCollection(
                getAllFoodDrinks,
                getSearchTerm
            );

            const getAllItems = (itemObj) => Object.keys(itemObj?.categories ?? {})
                .flatMap((key) => itemObj.categories[key]?.items ?? []);

            const allItems = [
                ...getAllItems(searchResultsProducts),
                ...getAllItems(searchResultsFoodDrink),
            ];

            const createCartLinesFromData = (items, branch) => {
                return items.map((item) => ({
                    branch,
                    item,
                }));
            };

            const productListChecker = createCartLinesFromData(allItems, { ...this.currentBranch });

            container.productAvailabilityChecker
                .sendRequest(productListChecker)
                .then((productsList) => {
                    const updatedProductsList = productsList.reduce((acc, product) => {
                        const matchingItem = allItems.find((item) => item.ID === product.productID);

                        if (matchingItem) {
                            acc.push({
                                ...product,
                                item: matchingItem,
                                suppressedItem: !!product.suppressedStock,
                            });
                        }
                        return acc;
                    }, []);

                    this.productsList = updatedProductsList?.length > 0 ? updatedProductsList : false;
                })
                .catch((error) => {
                    console.error('Product availability check failed:', error);
                    this.productsList = null;
                    this.error = this.l('SEARCH_MODAL.ERROR_BAD_REQUEST');
                })
                .finally(() => {
                    this.searching = false;
                });
        },

        /**
         *
         * @param {CartLine} cartItem
         * @param setDisableStockValidation
         *
         * @return {Promise<void>}
         */
        async itemClicked(cartItem, setDisableStockValidation) {
            if (this.isSellScreen()) {
                container.eventBus.broadcast(
                    EventNames.ADD_ITEM_TO_CART_REQUESTED,
                    {
                        item: cartItem.item,
                        quantity: 1,
                        extraInfo: null,
                        triggeredByItemButton: true,
                    }
                );
            } else {
                const newCart = container.cartService.createEmptyCart();
                const cartSet = await container.cartService.setNewCart(newCart, true);
                if (cartSet) {
                    await container.cartService.modifyCurrentCart((currentCart) => {
                        currentCart.sellState.autoAddProductItemID = cartItem.item.ID;
                    });
                }
            }

            if (setDisableStockValidation) {
                await container.cartService.modifyCurrentCart((currentCart) => {
                    currentCart.cartProductStockLevels.setDisableStockValidation(
                        cartItem.branch.ID,
                        cartItem.item.ID,
                        true
                    );
                });
            }

            if (!this.isSellScreen()) {
                container.navigationService.viewSell();
            }
            container.vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.SEARCH);
        },

        reset() {
            this.searchTerm = '';
            this.productsList = null;
            this.error = null;
            this.$refs.input.focus();
        },

        isSellScreen() {
            return this.$route.path === '/sell';
        },
    },
};
</script>
